import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import { buildAutomationChildTestId, buildAutomationOptionTestId, resolveAutomationTestId } from "../../InputField/testId";
import useFloatingPanel from "./useFloatingPanel";

const SelectDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className = "",
  disabled = false,
  showClear = true,
  onQueryChange,
  onReachEnd,
  isLoadingMore = false,
  scrollThreshold = 32,
  ariaLabel,
  error,
  showError = false,
  floating = true,
  panelZIndex,
  testId,
  id,
  "data-testid": dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const controlRef = useRef(null);
  const searchRef = useRef(null);
  const optionRefs = useRef([]);
  const listId = useId();
  const fieldTestId = resolveAutomationTestId(dataTestId, testId, id);
  const normalizedValue = value?.value ?? value ?? "";
  const panelStyle = useFloatingPanel(isOpen && floating, controlRef, { zIndex: panelZIndex });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      onQueryChange?.("");
      return undefined;
    }
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen, onQueryChange]);

  const hasGroupedOptions = useMemo(
    () => options.some((option) => Array.isArray(option?.options)),
    [options]
  );

  const flatOptions = useMemo(() => {
    if (!hasGroupedOptions) return options;
    return options.flatMap((group) => group?.options || []);
  }, [options, hasGroupedOptions]);

  const { sections, selectableOptions } = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let selectIndex = 0;

    if (!hasGroupedOptions) {
      const filtered = !needle
        ? options
        : options.filter((option) =>
          `${option.displayLabel ?? option.label ?? option.value ?? ""}`.toLowerCase().includes(needle)
        );
      return {
        sections: null,
        selectableOptions: filtered.map((option) => ({ ...option, _selectIndex: selectIndex++ })),
      };
    }

    const nextSections = options
      .map((group) => {
        const labelText = group?.label ?? group?.title ?? "";
        const groupOptions = group?.options || [];
        const filtered = !needle
          ? groupOptions
          : groupOptions.filter((option) =>
            `${option.displayLabel ?? option.label ?? option.value ?? ""} ${labelText}`.toLowerCase().includes(needle)
          );
        if (!filtered.length) return null;
        return {
          label: labelText,
          options: filtered.map((option) => ({
            ...option,
            _selectIndex: selectIndex++,
            _groupLabel: labelText,
          })),
        };
      })
      .filter(Boolean);

    return {
      sections: nextSections,
      selectableOptions: nextSections.flatMap((section) => section.options),
    };
  }, [options, query, hasGroupedOptions]);

  useEffect(() => {
    optionRefs.current = [];
  }, [selectableOptions]);

  const selectedOption = useMemo(
    () => flatOptions.find((option) => option.value === normalizedValue),
    [flatOptions, normalizedValue]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!selectableOptions.length) {
      setHighlightIndex(-1);
      return;
    }
    const selectedIndex = selectableOptions.findIndex((option) => option.value === normalizedValue);
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [selectableOptions, isOpen, normalizedValue, query]);

  useEffect(() => {
    if (!isOpen) return;
    const node = optionRefs.current[highlightIndex];
    node?.scrollIntoView?.({ block: "nearest" });
  }, [highlightIndex, isOpen]);

  const displayValue = selectedOption
    ? (selectedOption.displayLabel ?? selectedOption.label ?? selectedOption.value)
    : placeholder;

  const handleSelect = (option) => {
    if (!onChange || disabled) return;
    onChange(option?.value ?? "");
    setIsOpen(false);
    controlRef.current?.focus();
  };

  const handleClear = () => {
    if (!onChange || disabled) return;
    onChange("");
  };

  const moveHighlight = (delta) => {
    if (!selectableOptions.length) return;
    setHighlightIndex((prev) => {
      const base = prev < 0 ? 0 : prev;
      return (base + delta + selectableOptions.length) % selectableOptions.length;
    });
  };

  const selectHighlighted = () => {
    const option = selectableOptions[highlightIndex];
    if (option) handleSelect(option);
  };

  const handleControlKeyDown = (event) => {
    if (disabled) return;
    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      controlRef.current?.focus();
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      controlRef.current?.focus();
    }
  };

  const isError = Boolean(showError && error);
  const getOptionTestId = (option, fallback) =>
    buildAutomationOptionTestId(
      fieldTestId,
      option?.testId ?? option?.automationTestId ?? option?.value ?? option?.label ?? fallback
    );

  const getOptionRecordId = (option) =>
    option?.recordId ?? option?.dataRecordId ?? option?.record?._id;

  const handleListScroll = (event) => {
    if (!onReachEnd || isLoadingMore) return;
    const target = event.currentTarget;
    if (target.scrollHeight <= target.clientHeight) return;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom <= scrollThreshold) {
      onReachEnd();
    }
  };

  const renderOption = (option) => {
    const isSelected = option.value === normalizedValue;
    const isHighlighted = highlightIndex === option._selectIndex;
    return (
      <button
        key={option.value}
        type="button"
        ref={(node) => {
          optionRefs.current[option._selectIndex] = node;
        }}
        className={`account-search-dropdown__option ${isSelected ? "is-selected" : ""} ${isHighlighted ? "is-highlighted" : ""}`.trim()}
        onClick={() => handleSelect(option)}
        role="option"
        aria-selected={isSelected}
        data-record-id={getOptionRecordId(option) || undefined}
        data-testid={getOptionTestId(option, option._selectIndex + 1)}
      >
        <span>{option.label ?? option.value}</span>
      </button>
    );
  };

  return (
    <div className={`account-search-dropdown ${className}`.trim()} ref={containerRef} data-testid={fieldTestId}>
      {label ? <div className="account-search-dropdown__label">{label}</div> : null}
      <button
        type="button"
        ref={controlRef}
        className={`account-search-dropdown__control ${isOpen ? "is-open" : ""} ${disabled ? "is-disabled" : ""} ${isError ? "is-error" : ""}`.trim()}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleControlKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-label={ariaLabel || label || "Select option"}
        disabled={disabled}
        aria-invalid={isError}
        data-record-id={getOptionRecordId(selectedOption) || undefined}
        data-testid={buildAutomationChildTestId(fieldTestId, "toggle")}
      >
        <span className={`account-search-dropdown__value ${selectedOption ? "" : "is-placeholder"}`.trim()}>
          {displayValue}
        </span>
        <span className="account-search-dropdown__icon" aria-hidden="true">
          <IoChevronDown size={16} />
        </span>
      </button>
      {isOpen ? (
        <div
          className={`account-search-dropdown__panel ${floating ? "account-search-dropdown__panel--floating" : ""}`.trim()}
          style={panelStyle || undefined}
          data-testid={buildAutomationChildTestId(fieldTestId, "menu")}
        >
          <div className="account-search-dropdown__panel-header">
            <span className="account-search-dropdown__count">
              {selectedOption
                ? `Selected: ${selectedOption.displayLabel ?? selectedOption.label ?? selectedOption.value}`
                : "No selection"}
            </span>
            {showClear ? (
              <div className="account-search-dropdown__actions">
                <button type="button" onClick={handleClear} data-testid={buildAutomationChildTestId(fieldTestId, "clear")}>
                  Clear
                </button>
              </div>
            ) : null}
          </div>
          <input
            ref={searchRef}
            className="account-search-dropdown__search"
            type="text"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(event) => {
              const nextQuery = event.target.value;
              setQuery(nextQuery);
              onQueryChange?.(nextQuery);
            }}
            onKeyDown={handleSearchKeyDown}
            data-testid={buildAutomationChildTestId(fieldTestId, "input")}
          />
          <div
            className={hasGroupedOptions ? "account-search-dropdown__sections" : "account-search-dropdown__options"}
            role="listbox"
            id={listId}
            onScroll={handleListScroll}
          >
            {selectableOptions.length ? (
              hasGroupedOptions ? (
                sections?.map((section, sectionIndex) => (
                  <div key={`${section.label}-${sectionIndex}`} className="account-search-dropdown__section">
                    <div className="account-search-dropdown__section-title">{section.label}</div>
                    {section.options.map(renderOption)}
                  </div>
                ))
              ) : (
                selectableOptions.map(renderOption)
              )
            ) : (
              <div className="account-search-dropdown__empty" data-testid={buildAutomationChildTestId(fieldTestId, "empty")}>No matches</div>
            )}
            {isLoadingMore ? (
              <div className="account-search-dropdown__loading" data-testid={buildAutomationChildTestId(fieldTestId, "loading-more")}>
                Loading more...
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {isError ? (
        <div className="account-search-field__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default SelectDropdown;
